import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useJoinModal } from "@/components/landing/JoinModalProvider";

const NAV_LINKS = [
    { label: "Community", href: "#pillars" },
    { label: "Events", href: "#gallery" },
    { label: "FAQ", href: "#faq" },
];

export default function Header() {
    const { open } = useJoinModal();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/70 border-b border-black/5"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="logo-link"
                    className="font-display text-xl md:text-2xl font-black tracking-tight flex items-center gap-2"
                >
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#E54D2E] pulse-dot" />
                    Build&nbsp;Bout<span className="text-[#E54D2E]">.</span>
                </a>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV_LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={`nav-${link.label.toLowerCase()}`}
                            className="px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 rounded-full hover:bg-black/5 transition-colors"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={open}
                        data-testid="header-join-btn"
                        className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-zinc-950 text-white text-sm font-medium hover:bg-[#E54D2E] transition-colors group"
                    >
                        Register for Event
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full bg-zinc-950 text-white"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <Menu className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden backdrop-blur-xl bg-white/90 border-b border-black/5"
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                className="py-3 text-base font-medium text-zinc-800 border-b border-black/5 last:border-none"
                            >
                                {link.label}
                            </a>
                        ))}
                        <button
                            onClick={() => {
                                setMobileOpen(false);
                                open();
                            }}
                            data-testid="mobile-join-btn"
                            className="mt-3 h-11 rounded-full bg-zinc-950 text-white text-sm font-medium"
                        >
                            Register for Event
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
